import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  endOfQuarter,
  format,
  isWithinInterval,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from "date-fns";
import { ArrowDownLeft, ArrowUpRight, DollarSign, Trophy } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type RangeKey = "all" | "this_quarter" | "last_quarter";
type ChapterFilter = "all" | "washington" | "union";

const CHAPTER_LABELS: Record<string, string> = {
  washington: "Washington",
  union: "Union",
  all: "All Chapters",
};

interface MyReferralStatsProps {
  userId: string;
}

interface Referral {
  id: string;
  referred_by_id: string;
  referred_to_id: string;
  status: string;
  closed_amount: number | null;
  referral_date: string;
  chapter: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "hsl(45 93% 58%)",
  contacted: "hsl(217 91% 60%)",
  closed_won: "hsl(142 71% 45%)",
  closed_lost: "hsl(0 72% 51%)",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  contacted: "Contacted",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

const MyReferralStats = ({ userId }: MyReferralStatsProps) => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [myMemberId, setMyMemberId] = useState<string | null>(null);
  const [myChapter, setMyChapter] = useState<string | null>(null);
  const [additionalChapters, setAdditionalChapters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>("all");
  const [chapterFilter, setChapterFilter] = useState<ChapterFilter>("all");

  const load = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { data: me, error: meErr } = await supabase
        .from("members")
        .select("id, chapter, additional_chapters")
        .eq("user_id", userId)
        .maybeSingle();
      if (meErr) throw meErr;

      if (!me) {
        return;
      }
      setMyMemberId(me.id);
      setMyChapter(me.chapter);
      setAdditionalChapters((me.additional_chapters as string[]) || []);

      const { data, error: refErr } = await supabase
        .from("referrals")
        .select("id, referred_by_id, referred_to_id, status, closed_amount, referral_date, chapter");
      if (refErr) throw refErr;

      setReferrals(data || []);
    } catch (err: any) {
      setLoadError(err?.message || "Couldn't load stats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const isDualChapter = additionalChapters.length > 0;
  const availableChapters = isDualChapter && myChapter
    ? [myChapter, ...additionalChapters]
    : [];

  const stats = useMemo(() => {
    if (!myMemberId) {
      return {
        sentCount: 0,
        receivedCount: 0,
        wonCount: 0,
        totalEarned: 0,
        statusBreakdown: [] as { name: string; value: number; key: string }[],
        monthly: [] as { month: string; sent: number; received: number }[],
      };
    }

    const now = new Date();
    let windowStart: Date | null = null;
    let windowEnd: Date | null = null;
    if (range === "this_quarter") {
      windowStart = startOfQuarter(now);
      windowEnd = endOfQuarter(now);
    } else if (range === "last_quarter") {
      const lastQ = subQuarters(now, 1);
      windowStart = startOfQuarter(lastQ);
      windowEnd = endOfQuarter(lastQ);
    }
    const inRange = (r: Referral) =>
      !windowStart || !windowEnd
        ? true
        : isWithinInterval(new Date(r.referral_date + "T00:00:00"), { start: windowStart, end: windowEnd });

    const inChapter = (r: Referral) =>
      chapterFilter === "all" ? true : r.chapter === chapterFilter;

    const sent = referrals.filter((r) => r.referred_by_id === myMemberId && inRange(r) && inChapter(r));
    const received = referrals.filter((r) => r.referred_to_id === myMemberId && inRange(r) && inChapter(r));

    const totalEarned = received
      .filter((r) => r.status === "closed_won" && r.closed_amount)
      .reduce((sum, r) => sum + Number(r.closed_amount || 0), 0);

    const wonCount = received.filter((r) => r.status === "closed_won").length;

    // Status breakdown of referrals received (the ones the member acts on)
    const statusCounts = received.reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});
    const statusBreakdown = Object.entries(statusCounts).map(([key, value]) => ({
      key,
      name: STATUS_LABELS[key] || key,
      value,
    }));

    // Last 6 months sent vs received
    const months: { month: string; sent: number; received: number; key: string }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = startOfMonth(subMonths(new Date(), i));
      months.push({
        key: format(d, "yyyy-MM"),
        month: format(d, "MMM"),
        sent: 0,
        received: 0,
      });
    }
    const monthMap = new Map(months.map((m) => [m.key, m]));
    referrals.forEach((r) => {
      if (!inChapter(r)) return;
      const k = format(new Date(r.referral_date + "T00:00:00"), "yyyy-MM");
      const bucket = monthMap.get(k);
      if (!bucket) return;
      if (r.referred_by_id === myMemberId) bucket.sent++;
      if (r.referred_to_id === myMemberId) bucket.received++;
    });

    return {
      sentCount: sent.length,
      receivedCount: received.length,
      wonCount,
      totalEarned,
      statusBreakdown,
      monthly: months.map(({ key, ...rest }) => rest),
    };
  }, [referrals, myMemberId, range, chapterFilter]);

  const monthlyConfig: ChartConfig = {
    sent: { label: "Sent", color: "hsl(var(--primary))" },
    received: { label: "Received", color: "hsl(142 71% 45%)" },
  };

  const statusConfig: ChartConfig = stats.statusBreakdown.reduce((acc, s) => {
    acc[s.key] = { label: s.name, color: STATUS_COLORS[s.key] || "hsl(var(--muted))" };
    return acc;
  }, {} as ChartConfig);

  if (loading) {
    return <p className="text-center py-8 text-muted-foreground">Loading stats...</p>;
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-8 text-center space-y-3">
          <p className="text-sm text-muted-foreground">Couldn't load stats.</p>
          <p className="text-xs text-muted-foreground">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="text-sm underline text-primary hover:opacity-80"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  if (!myMemberId) {
    return <p className="text-center py-8 text-muted-foreground">No member profile found.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">My Referral Stats</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {isDualChapter && (
            <Select value={chapterFilter} onValueChange={(v) => setChapterFilter(v as ChapterFilter)}>
              <SelectTrigger className="w-[160px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Chapters</SelectItem>
                {availableChapters.map((c) => (
                  <SelectItem key={c} value={c}>
                    {CHAPTER_LABELS[c] || c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Sent</p>
                <p className="text-2xl font-bold">{stats.sentCount}</p>
              </div>
              <ArrowUpRight className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Received</p>
                <p className="text-2xl font-bold">{stats.receivedCount}</p>
              </div>
              <ArrowDownLeft className="h-5 w-5 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Closed Won</p>
                <p className="text-2xl font-bold">{stats.wonCount}</p>
              </div>
              <Trophy className="h-5 w-5 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Earned</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalEarned)}</p>
              </div>
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Activity (last 6 months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={monthlyConfig} className="h-[240px] w-full">
              <BarChart data={stats.monthly}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="sent" fill="var(--color-sent)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="received" fill="var(--color-received)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Received referrals by status</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.statusBreakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">
                No referrals received yet.
              </p>
            ) : (
              <ChartContainer config={statusConfig} className="h-[240px] w-full">
                <PieChart>
                  <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                  <Pie
                    data={stats.statusBreakdown}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {stats.statusBreakdown.map((entry) => (
                      <Cell key={entry.key} fill={STATUS_COLORS[entry.key]} />
                    ))}
                  </Pie>
                </PieChart>
              </ChartContainer>
            )}
            {stats.statusBreakdown.length > 0 && (
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {stats.statusBreakdown.map((s) => (
                  <div key={s.key} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: STATUS_COLORS[s.key] }}
                    />
                    <span className="text-muted-foreground">
                      {s.name} ({s.value})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyReferralStats;
